/**
 * ListDetailPage — shows items in a specific list with grid/table views.
 * Supports adding items from collection or as freeform (unlinked) entries.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, DetailModal, formatDate, Pill } from '@code-burrow/design-system';
import { UnifiedCollectionView, SearchOverlay } from '@code-burrow/tracker-core';
import { PlatformBadge } from '../adapters/gameCollectionAdapter.jsx';

// ── List Item Detail Modal ──────────────────────────────────────────────────

function ListItemDetailModal({ item, onClose, onRemove, onAddToCollection }) {
    if (!item) return null;

    const game = item._game;

    // Build subtitle parts
    const yearStr = item.releaseDate ? new Date(item.releaseDate).getFullYear().toString() : '';
    const creatorStr = game?.creator || '';
    const subtitleParts = [yearStr, creatorStr].filter(Boolean);

    // Source badge
    const source = game?.source || item.source;

    return (
        <DetailModal
            item={item}
            onClose={onClose}
            fields={[]}
            editable={false}
            header={{
                imageUrl: item._imageURL,
                imageAlt: item.title,
                title: item.title,
                subtitle: subtitleParts.length > 0 ? subtitleParts.join(' \u00b7 ') : undefined,
                tags: (game?.genres || []).slice(0, 4),
                extra: (
                    <>
                        {item._linked
                            ? <Pill className="bg-emerald-500/15 text-emerald-300 mt-2">In your collection</Pill>
                            : <Pill className="bg-yellow-500/15 text-yellow-500 mt-2">Not in collection</Pill>
                        }
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
                            {item.releaseDate && (
                                <span>{formatDate(item.releaseDate)}</span>
                            )}
                            {game?.storeURL && (
                                <a href={game.storeURL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-accent-primary hover:text-accent-hover transition-colors duration-150">
                                    {source === 'rawg' || source === 'xbox' ? 'View on RAWG' : 'View on Steam'}
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                </a>
                            )}
                            <span className="flex-1" /><PlatformBadge source={source} className="w-4 h-4" />
                        </div>
                    </>
                ),
            }}
            extraContent={
                <div className="px-4 pb-4 space-y-3">
                    {/* Action buttons */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => onRemove(item._listItemId)}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Remove from List
                        </button>
                        {!item._linked && (
                            <button
                                onClick={() => onAddToCollection(item)}
                                className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm rounded-md bg-accent-primary text-text-inverse hover:bg-accent-primary-hover transition-colors"
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                </svg>
                                Add to Collection
                            </button>
                        )}
                    </div>
                </div>
            }
        />
    );
}

export default function ListDetailPage({ backendService }) {
    const { listId } = useParams();
    const navigate = useNavigate();
    const [list, setList] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const record = await backendService.pocketbase.collection('lists').getOne(listId, { $autoCancel: false });
                setList(record);
            } catch (err) {
                console.error('ListDetailPage fetch error:', err);
                navigate('/lists');
            }
        })();
    }, [listId, backendService, navigate]);

    const handleRefresh = useCallback(() => {
        setRefreshKey((k) => k + 1);
    }, []);

    const handleRemove = useCallback(async (listItemId) => {
        await backendService.deleteListItem(listItemId);
        setSelectedItem(null);
        handleRefresh();
    }, [backendService, handleRefresh]);

    const handleAddToCollection = useCallback((item) => {
        setSelectedItem(null);
        setAddOpen(true);
    }, []);

    // Fetch all games for the SearchOverlay's local items
    const [allGames, setAllGames] = useState([]);
    useEffect(() => {
        (async () => {
            const result = await backendService.getAllGames();
            if (result.success) setAllGames(result.entries || []);
        })();
    }, [backendService, refreshKey]);

    // Add a collection item to this list
    const handleAddFromCollection = useCallback(async (item) => {
        await backendService.addListItem(listId, {
            itemId: item.id,
            title: item.title,
            imageURL: item.imageURL || '',
        });
        handleRefresh();
    }, [backendService, listId, handleRefresh]);

    // External game search for adding new items to the list
    const handleExternalSearch = useCallback(async (query) => {
        try {
            const result = await backendService.searchGames(query);
            if (result.success && result.searchResults?.games) {
                return { results: result.searchResults.games.slice(0, 20) };
            }
            return { results: [], error: result.error || 'No results' };
        } catch (err) {
            return { results: [], error: err.message };
        }
    }, [backendService]);

    // Add a game from external search to collection AND to this list
    const handleExternalAction = useCallback(async (item) => {
        const result = await backendService.addGameFromData(item);
        if (!result.success) throw new Error(result.error);

        const gamePbId = result.pbId || result.entry?.id;
        if (gamePbId) {
            await backendService.addListItem(listId, {
                itemId: gamePbId,
                title: item.title,
                imageURL: item.imageURL || '',
            });
        }
        handleRefresh();
    }, [backendService, listId, handleRefresh]);

    if (!list) return null;

    const adapter = listCollectionAdapter(backendService, listId, refreshKey, handleRemove, setSelectedItem);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/lists')} className="p-1 text-text-tertiary hover:text-text-primary transition-colors">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                        </svg>
                    </button>
                    <h1 className="text-3xl font-bold font-display text-text-primary">
                        {list.emoji && <span className="mr-2">{list.emoji}</span>}
                        {list.name}
                    </h1>
                </div>
                <button
                    onClick={() => setAddOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-accent-primary text-text-inverse hover:bg-accent-primary-hover transition-colors"
                >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Item
                </button>
            </div>

            <UnifiedCollectionView
                collectionAdapter={adapter}
                refreshKey={`${refreshKey}-${listId}`}
            />

            <SearchOverlay
                open={addOpen}
                onClose={() => setAddOpen(false)}
                items={allGames}
                onSelect={handleAddFromCollection}
                placeholder="Search games..."
                onExternalSearch={handleExternalSearch}
                onExternalAction={handleExternalAction}
                externalSearchLabel="Search online"
                externalItemKey={(item) => item.steamID || item.id}
                renderResult={(item) => (
                    <>
                        {item.imageURL ? (
                            <img src={item.imageURL} alt={item.title} className="w-14 h-10 object-cover rounded flex-shrink-0" />
                        ) : (
                            <div className="w-14 h-10 bg-bg-tertiary rounded flex-shrink-0 flex items-center justify-center">
                                <span className="text-lg">🎮</span>
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-text-primary truncate font-body">{item.title}</p>
                            {item.releaseDate && (
                                <p className="text-xs text-text-secondary font-body">{new Date(item.releaseDate).getFullYear()}</p>
                            )}
                        </div>
                    </>
                )}
                renderExternalResult={(item, { onAction, isLoading, isDone }) => (
                    <>
                        {item.imageURL ? (
                            <img src={item.imageURL} alt={item.title} className="w-14 h-10 object-cover rounded flex-shrink-0" />
                        ) : (
                            <div className="w-14 h-10 bg-bg-tertiary rounded flex-shrink-0 flex items-center justify-center">
                                <span className="text-lg">🎮</span>
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-text-primary truncate font-body">{item.title}</p>
                                {item.source && (
                                    <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${
                                        item.source === 'rawg' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-blue-500/15 text-blue-400'
                                    }`}>{item.source === 'rawg' ? 'RAWG' : 'Steam'}</span>
                                )}
                            </div>
                            {item.genres?.length > 0 && (
                                <p className="text-xs text-text-secondary font-body">{item.genres.slice(0, 2).join(', ')}</p>
                            )}
                        </div>
                        <div className="flex-shrink-0">
                            {isDone ? (
                                <span className="text-xs text-green-500 font-medium">Added ✓</span>
                            ) : isLoading ? (
                                <div className="w-4 h-4 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onAction(); }}
                                    className="p-1 text-text-tertiary hover:text-accent-primary transition-colors"
                                >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </>
                )}
            />

            <ListItemDetailModal
                item={selectedItem}
                onClose={() => setSelectedItem(null)}
                onRemove={handleRemove}
                onAddToCollection={handleAddToCollection}
            />
        </div>
    );
}

/**
 * Adapter for rendering list items inside UnifiedCollectionView.
 */
function listCollectionAdapter(backendService, listId, _refreshKey, onRemove, onSelect) {
    return {
        storageKey: `videogame-list-${listId}`,
        defaultSortField: 'position',
        defaultSortDirection: 'asc',

        getImageUrl: (item) => item._imageURL || item.imageURL || null,

        fetchItems: async () => {
            const result = await backendService.getListItems(listId);
            const listItems = result.items || [];

            // Enrich linked items with game data
            let allGames = [];
            const linkedIds = listItems.filter((li) => li.itemId).map((li) => li.itemId);
            if (linkedIds.length > 0) {
                const gameResult = await backendService.getAllGames();
                allGames = gameResult.success ? (gameResult.entries || []) : [];
            }

            const bgMap = Object.fromEntries(allGames.map((g) => [g.id, g]));

            return listItems.map((li) => {
                const linked = li.itemId ? bgMap[li.itemId] : null;
                const resolvedImage = linked?.imageURL || li.imageURL || null;

                return {
                    ...li,
                    // Merge game data for linked items
                    title: linked?.title || li.title,
                    releaseDate: linked?.releaseDate || null,
                    genres: linked?.genres || [],
                    source: linked?.source || null,
                    _imageURL: resolvedImage,
                    _linked: !!linked,
                    _game: linked || null,
                    // Keep list item fields
                    _listItemId: li.id,
                    notes: li.notes,
                    position: li.position,
                };
            });
        },

        filterFields: [],

        sortFields: [
            { key: 'position', label: 'Position', getValue: (item) => item.position ?? 0 },
            { key: 'title', label: 'Title', getValue: (item) => (item.title ?? '').toLowerCase() },
        ],

        renderGridItem: (item) => {
            return (
                <Card
                    key={item._listItemId || item.id}
                    imageUrl={item._imageURL}
                    imageAlt={item.title}
                    title={item.title}
                    subtitle={item.releaseDate ? `(${new Date(item.releaseDate).getFullYear()})` : undefined}
                    onClick={() => onSelect(item)}
                    statusPill={
                        !item._linked ? (
                            <div className="mt-1">
                                <Pill className="bg-yellow-500/15 text-yellow-500">Not in collection</Pill>
                            </div>
                        ) : null
                    }
                />
            );
        },

        renderTableColumns: [
            {
                key: 'title',
                header: 'Title',
                sortable: 'title',
                renderCell: (item) => (
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-text-primary">{item.title}</span>
                        {!item._linked && (
                            <Pill className="bg-yellow-500/15 text-yellow-500">Not in collection</Pill>
                        )}
                    </div>
                ),
            },
            {
                key: 'notes',
                header: 'Notes',
                renderCell: (item) => (
                    <span className="text-text-secondary text-xs">{item.notes || '-'}</span>
                ),
            },
            {
                key: 'position',
                header: '#',
                sortable: 'position',
                width: '60px',
                renderCell: (item) => (
                    <span className="text-text-tertiary text-xs">{(item.position ?? 0) + 1}</span>
                ),
            },
            {
                key: 'remove',
                header: '',
                width: '40px',
                renderCell: (item) => (
                    <button
                        onClick={(e) => { e.stopPropagation(); onRemove(item._listItemId); }}
                        className="p-1 rounded text-text-tertiary hover:text-red-400 transition-colors"
                        title="Remove from list"
                    >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                ),
            },
        ],

        emptyState: {
            title: 'This list is empty',
            description: 'Add items from your collection or create new entries',
        },

        actions: [],
    };
}
